import { useEffect, useRef, useState } from 'react';
import { Clock, History, X, CheckSquare, FileText, FileQuestion, PhoneCall, Building2, CalendarClock, ShoppingCart, Users2, FolderKanban, Truck, ClipboardList, MousePointerClick, ChevronDown } from 'lucide-react';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { useV3Prefs } from './V3PrefsContext';
import { useV3Alerts } from './V3AlertsContext';
import { TasksTab, EightXEightTab, ScheduledCallsTab, StockTab, PiTab, OrdersTab } from './V3TasksPanel';
import V3BarTip from './V3BarTip';
import { CollapseIcon, Pin } from './V3Icons';

// Same icons the old Open / Recent strip used for each kind of activity (components/ActivityBar.jsx).
const TYPE_ICONS = {
  proforma: FileText,
  quote: FileQuestion,
  eight_by_eight_calls: PhoneCall,
  add_to_crm: Building2,
  add_call_to_crm: PhoneCall,
  schedule_call: CalendarClock,
  stock_request: ShoppingCart,
  customer_management: Users2,
  project_management: FolderKanban,
  b29_transfer: Truck,
};

// The part of the application each activity belongs to: shown on the first line of its entry, next to the small icon.
const TYPE_LABELS = {
  proforma: 'Pro Forma',
  quote: 'Quote',
  eight_by_eight_calls: '8x8 Calls',
  add_to_crm: 'Add to CRM',
  add_call_to_crm: 'Add Call',
  schedule_call: 'Schedule Call',
  stock_request: 'Stock Request',
  customer_management: 'Customer Management',
  project_management: 'Project Management',
  b29_transfer: 'B29 Transfer',
};

// One coloured icon of the right bar: down the minimised bar, and in the row under the pills on the open bar. The count sits on its corner, and
// the icon of the open view has a small bar under it.
const TabIcon = ({ tab, onClick, testId, selected }) => (
  <button
    type="button"
    role={selected === undefined ? undefined : 'tab'}
    aria-selected={selected}
    className={`v3-tabicon v3-tabicon--${tab.tone} ${selected ? 'v3-tabicon--on' : ''}`}
    onClick={onClick}
    data-v3-tip={tab.label}
    aria-label={tab.label}
    data-testid={testId}
  >
    <tab.Icon className="v3-i" />
    {/* One count on the bottom left of the icon; the Tasks icon has two: today's tasks bottom left, overdue tasks bottom right. */}
    {(tab.badges || (tab.count > 0 ? [{ side: 'left', value: tab.count }] : [])).filter((badge) => badge.value > 0).map((badge) => (
      <span key={badge.side} className={`v3-nav-count v3-nav-count--corner v3-nav-count--${badge.side} ${tab.alert ? 'v3-nav-count--alert' : ''}`}>{badge.value}</span>
    ))}
  </button>
);

// The right edge bar. Open, it has the arrow and two pills at the top, Recents and Q & O (quotes and orders), and under them a row of coloured icons that
// is always there, whatever page is being viewed: Tasks (blue), 8x8 calls (lime), Scheduled calls (purple), Stock requests (amber) and PI counts (teal).
// Each of them, Recents and Q & O is its own view: the bar shows one at a time, chosen by its pill or its icon. The same icons run down the minimised bar
// and open the bar on their view. Which of them a person gets is decided by Settings > Dashboard > Notification Bar (useV3Alerts().show), as on the Home page.
// Each Open or Recent entry has the small icon and the part of the application on its first line and the name underneath. Open entries have the
// close X at the top right and an orange icon; Recent entries have a purple icon and no X.
const TONE_COLORS = { orange: '#ee7203', blue: '#2563eb', lime: '#cbdd2c', purple: '#9333ea', amber: '#16a34a', teal: '#475569', indigo: '#4f46e5' };

// On a small screen (an iPad) the right bar's views (Recents, Q & O and every icon) are chosen from one drop-down, instead of a pair of pills and a row of icons.
const ViewSwitcher = ({ tabs, activeId, onPick }) => {
  const [open, setOpen] = useState(false);
  const holder = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (holder.current && !holder.current.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);
  const active = tabs.find((tab) => tab.id === activeId) || tabs[0];
  const countOf = (tab) => (tab.badges ? tab.badges.reduce((sum, badge) => sum + (badge.value || 0), 0) : (tab.count || 0));
  return (
    <div className="v3-viewsw" ref={holder} data-testid="v3-right-view-switcher">
      <button
        type="button"
        className="v3-viewsw-button"
        style={{ '--vs': TONE_COLORS[active.tone] }}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="v3-right-view-button"
      >
        <active.Icon className="v3-i" />
        <span className="v3-viewsw-label">{active.label}</span>
        {countOf(active) > 0 && <span className="v3-viewsw-count">{countOf(active)}</span>}
        <ChevronDown className={`v3-viewsw-caret ${open ? 'is-open' : ''}`} />
      </button>
      {open && (
        <div className="v3-viewsw-list" role="listbox" data-testid="v3-right-view-list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="option"
              aria-selected={tab.id === active.id}
              className={`v3-viewsw-item ${tab.id === active.id ? 'is-on' : ''}`}
              style={{ '--vs': TONE_COLORS[tab.tone] }}
              onClick={() => { onPick(tab.id); setOpen(false); }}
              data-testid={`v3-right-view-${tab.id}`}
            >
              <tab.Icon className="v3-i" />
              <span className="v3-viewsw-label">{tab.label}</span>
              {countOf(tab) > 0 && <span className="v3-viewsw-count">{countOf(tab)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const V3RightBar = ({ overlay = false }) => {
  const { modes, toggleBar, barMode, setBarMode, setDefaultState, closeOverlay, rightTab, setRightTab, rightCanPin, smallScreen } = useV3Prefs();
  const { openActivities, recentActivities, reopenOpen, reopenRecent, removeOpen } = useActivityBar();
  const alerts = useV3Alerts();
  const mode = overlay ? 'open' : modes.right;
  const barRef = useRef(null);
  // The two buttons at the foot: Pinned and Closes-when-I-click-away. A button that is lit is the way this bar is working now; pressing it MINIMISES the bar (it keeps working that
  // way, and a pinned bar remembers it is minimised). It never changes how the bar works, so a pinned bar stays pinned. Pressing the one that is not lit makes the bar work that way
  // (a bar that is open over the page is then pinned or closes-when-I-click-away: part of the page). 'Over the page' is chosen in My preferences.
  const pinnedNow = barMode.right === 'pinned' && !overlay;
  const autoNow = barMode.right === 'auto' && !overlay;

  if (mode === 'hidden') {
    return (
      <button
        type="button"
        className="v3-handle v3-handle--right"
        onClick={() => toggleBar('right')}
        aria-label="Open Recents and Tasks"
        data-testid="v3-right-handle"
      >
        <CollapseIcon side="right" open={false} />
      </button>
    );
  }

  const className = [
    'v3-glass', 'v3-glass--flat', 'v3-bar', 'v3-bar--right',
    mode === 'strip' ? 'v3-bar--strip' : '',
    overlay ? 'v3-bar--overlay' : '',
  ].filter(Boolean).join(' ');

  const reopen = (fn, id) => { fn(id); if (overlay) closeOverlay(); };
  const total = openActivities.length + recentActivities.length;
  // A minimised icon opens the bar on its own view.
  const openTab = (id) => { setRightTab(id); toggleBar('right'); };

  const { show } = alerts;
  const tabs = [
    { id: 'recents', label: 'Recents', Icon: Clock, tone: 'orange', count: 0 },   // no count on the Recents logo: it stays a plain orange clock
    show.tasks && { id: 'tasks', label: 'Tasks', Icon: CheckSquare, tone: 'blue', count: alerts.overdueTasks.length + alerts.todayTasks.length, badges: [{ side: 'left', value: alerts.todayTasks.length }, { side: 'right', value: alerts.overdueTasks.length }] },
    show.eightXEight && { id: 'eightxeight', label: '8x8 calls', Icon: PhoneCall, tone: 'lime', count: alerts.calls8x8MadeToday.length },
    show.calls && { id: 'calls', label: 'Scheduled calls', Icon: CalendarClock, tone: 'purple', count: alerts.calls.length, alert: alerts.overdueCalls.length > 0 },
    show.stock && { id: 'stock', label: 'Stock requests', Icon: ShoppingCart, tone: 'amber', count: alerts.stockSummary?.total_active || 0 },
    show.pi && { id: 'pi', label: 'PI counts', Icon: ClipboardList, tone: 'teal', count: alerts.piCounts.length },
    show.orders && { id: 'orders', label: 'Sales', Icon: FileText, tone: 'indigo', count: 0 },
  ].filter(Boolean);
  // One view at a time. Recents and Q & O are chosen by their pills, every other kind by its icon in the row under them.
  const viewTabs = tabs.filter((tab) => tab.id !== 'recents' && tab.id !== 'orders');
  const activeId = tabs.some((tab) => tab.id === rightTab) ? rightTab : 'recents';
  const ordersTab = tabs.find((tab) => tab.id === 'orders');
  const recentsList = (
      <>
        {openActivities.length > 0 && (
          <>
            <div className="v3-nav-label">Open</div>
            {openActivities.map((activity) => {
              const Icon = TYPE_ICONS[activity.type] || Clock;
              const kind = TYPE_LABELS[activity.type] || activity.subtitle || 'Open';
              return (
                <div key={activity.id} className="v3-item v3-item--open" data-testid={`v3-open-${activity.id}`}>
                  <button type="button" className="v3-item-button" onClick={() => reopen(reopenOpen, activity.id)} title={activity.subtitle || activity.title}>
                    <span className="v3-item-text">
                      <span className="v3-item-kind"><Icon className="v3-i v3-i--sm" /><span>{kind}</span></span>
                      <span className="v3-item-title">{activity.title}</span>
                    </span>
                  </button>
                  <button type="button" className="v3-item-remove" onClick={() => removeOpen(activity.id)} aria-label={`Close ${activity.title}`} data-testid={`v3-open-close-${activity.id}`}>
                    <X className="v3-i v3-i--sm" />
                  </button>
                </div>
              );
            })}
          </>
        )}
        {recentActivities.length > 0 && (
          <>
            <div className="v3-nav-label">Recent</div>
            {recentActivities.map((activity) => {
              const Icon = TYPE_ICONS[activity.type] || History;
              const kind = TYPE_LABELS[activity.type] || activity.subtitle || 'Recent';
              return (
                <div key={activity.id} className="v3-item v3-item--recent" data-testid={`v3-recent-${activity.id}`}>
                  <button type="button" className="v3-item-button" onClick={() => reopen(reopenRecent, activity.id)} title={activity.subtitle || activity.title}>
                    <span className="v3-item-text">
                      <span className="v3-item-kind"><Icon className="v3-i v3-i--sm" /><span>{kind}</span></span>
                      <span className="v3-item-title">{activity.title}</span>
                    </span>
                  </button>
                </div>
              );
            })}
          </>
        )}
        {total === 0 && (
          <p className="v3-empty" data-testid="v3-right-empty">Nothing open or recent yet. Quotes, calls and customers you work on appear here.</p>
        )}
      </>
  );

  const viewBody = () => {
    switch (activeId) {
      case 'tasks': return <TasksTab />;
      case 'eightxeight': return <EightXEightTab />;
      case 'calls': return <ScheduledCallsTab />;
      case 'stock': return <StockTab />;
      case 'pi': return <PiTab />;
      case 'orders': return <OrdersTab />;
      default: return recentsList;
    }
  };

  return (
    <aside ref={barRef} className={className} data-testid={overlay ? 'v3-right-bar-overlay' : 'v3-right-bar'} aria-label="Recents and Tasks">
      <V3BarTip barRef={barRef} side="left" />
      {mode === 'strip' ? (
        <div className="v3-bar-body v3-bar-body--strip v3-bar-body--strip-top v3-rail">
          <div className="v3-rail-recents">
            <button
              type="button"
              className="v3-collapse"
              onClick={() => toggleBar('right')}
              aria-label="Open Recents and Tasks"
              data-v3-tip="Open Recents and Tasks"
              data-testid="v3-right-collapse"
            >
              <CollapseIcon side="right" open={false} />
            </button>
            <TabIcon tab={tabs[0]} onClick={() => openTab('recents')} testId="v3-right-strip-recents" />
          </div>
          {tabs.length > 1 && <div className="v3-rail-sep" aria-hidden="true" />}
          {tabs.slice(1).map((tab) => (
            <TabIcon key={tab.id} tab={tab} onClick={() => openTab(tab.id)} testId={`v3-right-strip-${tab.id}`} />
          ))}
        </div>
      ) : (
        <div className="v3-bar-body v3-bar-body--tabs">
          {smallScreen ? (
            <div className="v3-tabs v3-tabs--small">
              <button type="button" className="v3-collapse" onClick={() => toggleBar('right')} aria-label="Collapse Recents and Tasks" title="Collapse Recents and Tasks" data-testid="v3-right-collapse-top">
                <CollapseIcon side="right" open={true} />
              </button>
              <ViewSwitcher tabs={tabs} activeId={activeId} onPick={setRightTab} />
            </div>
          ) : (
          <>
          <div className="v3-tabs" role="tablist" aria-label="Right bar">
            <button type="button" className="v3-collapse" onClick={() => toggleBar('right')} aria-label="Collapse Recents and Tasks" title="Collapse Recents and Tasks" data-testid="v3-right-collapse-top">
              <CollapseIcon side="right" open={true} />
            </button>
            <button type="button" role="tab" aria-selected={activeId === 'recents'} className={`v3-tab ${activeId === 'recents' ? 'v3-tab--on' : ''}`} onClick={() => setRightTab('recents')} data-testid="v3-right-tab-recents">
              Recents
            </button>
            {ordersTab && (
              <button type="button" role="tab" aria-selected={activeId === 'orders'} className={`v3-tab ${activeId === 'orders' ? 'v3-tab--on' : ''}`} onClick={() => setRightTab('orders')} data-v3-tip="Quotes and orders" data-testid="v3-right-tab-orders">
                Sales
              </button>
            )}
          </div>
          {viewTabs.length > 0 && (
            <div className="v3-tabicons" role="tablist" aria-label="Views" data-testid="v3-right-icon-row">
              {viewTabs.map((tab) => (
                <TabIcon key={tab.id} tab={tab} selected={tab.id === activeId} onClick={() => setRightTab(tab.id)} testId={`v3-right-tab-${tab.id}`} />
              ))}
            </div>
          )}
          </>
          )}
          <div className="v3-bar-scroll">
            {viewBody()}
          </div>
        </div>
      )}

      <div className={`v3-bar-foot v3-bar-foot--right ${mode === 'strip' ? 'v3-bar-foot--strip' : ''}`}>
        {/* How the bar works: pinned (open, part of the page, stays), or closes by itself when you click away (open and part of the page, folds when you click outside
            it). With neither on, the bar is an icon strip that opens over the page. */}
        {mode !== 'strip' && rightCanPin && (
          <>
            <button
              type="button"
              className={`v3-collapse ${pinnedNow ? 'v3-collapse--on' : ''}`}
              onClick={pinnedNow ? () => toggleBar('right') : () => { setBarMode('right', 'pinned'); setDefaultState('right', 'open'); }}
              aria-label="Pin Recents and Tasks"
              aria-pressed={pinnedNow}
              data-v3-tip={pinnedNow ? 'Pinned: stays open and moves the page. Click to minimise it (it stays pinned)' : 'Pin: keep it open and moving the page'}
              data-testid="v3-right-pin"
            >
              <Pin className="v3-i v3-i--sm" />
            </button>
            <button
              type="button"
              className={`v3-collapse ${autoNow ? 'v3-collapse--on' : ''}`}
              onClick={autoNow ? () => toggleBar('right') : () => setBarMode('right', 'auto')}
              aria-label="Close Recents and Tasks when I click away"
              aria-pressed={autoNow}
              data-v3-tip={autoNow ? 'Closes when you click away. Click to minimise it' : 'Open as part of the page, and close by itself when I click away'}
              data-testid="v3-right-auto"
            >
              <MousePointerClick className="v3-i v3-i--sm" />
            </button>
          </>
        )}
      </div>
    </aside>
  );
};

export default V3RightBar;
